<template>
  <div class="flex-grow-1 position-relative" data-test="marketSignalSearchBar">
    <InputGroup>
      <Dropdown
        v-if="!isMobileDevice"
        v-model="selectedType"
        :options="typeOptions"
        panelClass="z-index-2000"
        :panelStyle="{ marginTop: '5px' }"
        style="max-width: 200px"
        optionLabel="label"
        data-test="marketSignalSearchType"
        :pt="{
          list: {
            style:
              'margin-bottom: 0px; padding: 0px; font-family: nunito-sans;',
          },
          input: { style: 'font-family: nunito-sans;' },
        }"
      />
      <InputGroupAddon class="addon">
        <i class="pi pi-search"></i>
      </InputGroupAddon>
      <AutoComplete
        v-model="searchTerm"
        :suggestions="groups"
        @complete="search"
        @item-select="onItemSelect"
        @keydown.enter="onEnterPress"
        :panelStyle="{ marginTop: '5px' }"
        panelClass="z-index-2000"
        optionLabel="title"
        optionGroupLabel="label"
        optionGroupChildren="items"
        style="min-width: 230px"
        :focusOnHover="false"
        :autoOptionFocus="false"
        scrollHeight="550px"
        placeholder="Search for a dashboard"
        data-test="marketSignalSearchInput"
        :pt="{
          list: { style: 'margin-bottom: 0px;' },
          itemGroup: { style: 'padding: 0' },
          input: {
            style: 'outline: none; box-shadow: none; font-family: nunito-sans;',
          },
        }"
      >
        <template #header>
          <div class="search-panel-header">Results for "{{ lastTerm }}"</div>
        </template>
        <template #empty>
          <div class="px-3 py-2 nunito" data-test="marketSignalSearchEmpty">
            No dashboards match "{{ lastTerm }}"<template
              v-if="selectedType.key"
            >
              in {{ selectedType.label }}</template
            >.
          </div>
        </template>
        <template #optiongroup="slotProps">
          <div class="search-panel-group">{{ slotProps.option.label }}</div>
        </template>
        <template #option="slotProps">
          <div
            class="d-flex align-items-center gap-3 nunito"
            :data-test="`searchResult-${slotProps.option.key}`"
          >
            <span class="result-icon">
              <i class="pi pi-chart-bar" aria-hidden="true"></i>
            </span>
            <div class="min-w-0">
              <div class="result-title">{{ slotProps.option.title }}</div>
              <div class="result-meta">
                {{ categoryLabel(slotProps.option.access_grant_key) }}
              </div>
            </div>
          </div>
        </template>
      </AutoComplete>
    </InputGroup>
  </div>
</template>

<script>
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import AutoComplete from "primevue/autocomplete";
import Dropdown from "primevue/dropdown";
import isMobile from "@/composables/datatable/isMobile";
import { useMarketSignalStore } from "@/store/marketSignal";
import { categoryLabel } from "@/services/marketSignalCategories";

const ALL_TYPES = { key: null, label: "All types" };

/**
 * Navbar search over the company's MarketSignal dashboards. Mirrors
 * UniversalSearchBar's PrimeVue layout so keyboard, click-outside and
 * grouping behave the same as RepSignal's provider search.
 */
export default {
  name: "MarketSignalSearchBar",
  components: { InputGroup, InputGroupAddon, AutoComplete, Dropdown },
  setup() {
    const { isMobileDevice } = isMobile("sm");
    const marketSignal = useMarketSignalStore();
    return { isMobileDevice, marketSignal, categoryLabel };
  },
  data() {
    return {
      searchTerm: "",
      lastTerm: "",
      selectedType: ALL_TYPES,
      groups: [],
    };
  },
  computed: {
    typeOptions() {
      return [ALL_TYPES, ...(this.marketSignal.categories || [])];
    },
  },
  created() {
    this.marketSignal.load();
  },
  methods: {
    search(event) {
      const term = (event?.query ?? "").trim();
      this.lastTerm = term;
      const categories = (this.marketSignal.categories || []).filter(
        (c) => !this.selectedType.key || c.key === this.selectedType.key
      );
      this.groups = categories
        .map((c) => ({
          label: c.label,
          items: this.marketSignal.search(term, c.key),
        }))
        .filter((g) => g.items.length > 0);
    },
    goToDashboard(dashboard) {
      this.searchTerm = "";
      this.groups = [];
      this.$router.push({
        name: "marketSignalDashboard",
        params: { key: dashboard.key },
      });
    },
    onItemSelect(event) {
      if (event?.value?.key) {
        this.goToDashboard(event.value);
      }
    },
    onEnterPress() {
      // AutoComplete sets the model to the selected object on Enter over a
      // highlighted option; otherwise fall back to the first match.
      if (typeof this.searchTerm === "object" && this.searchTerm?.key) {
        this.goToDashboard(this.searchTerm);
        return;
      }
      const first = this.groups[0]?.items?.[0];
      if (first) {
        this.goToDashboard(first);
      }
    },
  },
};
</script>

<style src="@/assets/searchPanel.css"></style>

<style scoped>
.nunito {
  font-family: nunito-sans, sans-serif;
}
.addon {
  background-color: var(--vt-c-white);
}
.result-icon {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--S2NOceanBlue);
  color: #fff;
}
.result-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--S2NOceanBlue);
}
.result-meta {
  font-size: 12px;
  color: #7a8b99;
}
.min-w-0 {
  min-width: 0;
}
:deep(.p-autocomplete-input) {
  border-left: none;
}
:deep(.p-autocomplete-input:focus),
:deep(.p-autocomplete-input:hover) {
  border: 1px solid #2196f3;
}
</style>
